/**
 * Catalogo dei blocchi del builder email.
 *
 * Un blocco e' { id, type, styles, settings?, content?, columns?, editable? }.
 * Per i blocchi con `render` la fonte di verita' sono i `settings`: il `content`
 * (HTML email-safe: tabelle + stili inline) viene sempre rigenerato da qui, sia
 * alla creazione sia quando cambiano settings o styles. `fields` dichiara i campi
 * che il pannello mostra per quei settings: e' l'unico schema, e sara' il contratto
 * dato al modello AI. Per i blocchi `editable` (testo libero) la fonte e' il content.
 *
 * Regole di compatibilita' (Outlook usa il motore di Word):
 * tabelle con larghezze esplicite, stili inline, niente flex/grid/margin su td.
 * Le colonne affiancate usano il pattern "ibrido" (hybridRow): inline-block con
 * max-width, e una tabella fantasma nei commenti condizionali per Outlook.
 */

var _counter = 0
function uid() {
	return 'block-' + Date.now() + '-' + (++_counter)
}

function escapeHtml(value) {
	return String(value == null ? '' : value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

function nl2br(text) {
	return escapeHtml(text).replace(/\r?\n/g, '<br />')
}

function tableWrap(tdStyle, inner) {
	return '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr><td style="' + tdStyle + '">' + inner + '</td></tr></table>'
}

var DEFAULT_FONT = 'Arial,Helvetica,sans-serif'
var BRAND_COLOR = '#009fc7'

// Proprieta' che un titolo eredita dal blocco quando titleStyles non le ridefinisce: sono
// scelte che valgono per tutta la sezione. Dimensione e peso NO: sono cio' che separa il
// titolo dal corpo, e prenderli da styles rende la sezione tipograficamente piatta.
var TITLE_INHERITED = ['font-family', 'line-height', 'color', 'text-align', 'letter-spacing', 'text-transform']

/**
 * Input:  block, nome della proprieta', se vale per il titolo
 * Fa:     risolve una proprieta' fra le due scale: il corpo legge styles, il titolo legge
 *         titleStyles e ricade su styles solo per le proprieta' di sezione
 * Output: valore o null
 */
function styleValue(block, prop, heading) {
	var s = block.styles || {}
	if (!heading) return s[prop] || null
	var t = block.titleStyles || {}
	if (t[prop] != null && t[prop] !== '') return t[prop]
	return TITLE_INHERITED.indexOf(prop) === -1 ? null : (s[prop] || null)
}

/**
 * Input:  testo, block, se e' il titolo
 * Fa:     applica text-transform AL TESTO. Il motore di Word (Outlook per Windows) non
 *         supporta la proprieta' CSS: se la maiuscola vive solo nello stile, li' non si vede.
 *         Scritta nel testo funziona ovunque, e la regola CSS resta come rinforzo
 * Output: testo trasformato
 */
function caseText(block, heading, value) {
	var mode = styleValue(block, 'text-transform', heading)
	var text = String(value == null ? '' : value)
	if (mode === 'uppercase') return text.toUpperCase()
	if (mode === 'lowercase') return text.toLowerCase()
	if (mode === 'capitalize') return text.replace(/(^|\s)(\S)/g, function(m, sep, chr) { return sep + chr.toUpperCase() })
	return text
}

/**
 * Input:  block, opts { size, color, weight?, margin?, align?, heading? }
 * Fa:     stile inline per un testo. Il corpo legge block.styles, il titolo block.titleStyles
 *         con ricaduta su styles per le proprieta' di sezione (TITLE_INHERITED)
 * Output: stringa style
 */
function textStyle(block, opts) {
	var pick = function(prop) { return styleValue(block, prop, opts.heading) }

	var parts = [
		'margin:' + (opts.margin || '0'),
		'font-family:' + (pick('font-family') || DEFAULT_FONT),
		'font-size:' + (pick('font-size') || opts.size),
		'line-height:' + (pick('line-height') || (opts.heading ? '1.25' : '1.5')),
		'color:' + (pick('color') || opts.color),
		'text-align:' + (pick('text-align') || opts.align || 'left'),
		'font-weight:' + (pick('font-weight') || opts.weight || 'normal')
	]

	var spacing = pick('letter-spacing')
	if (spacing) parts.push('letter-spacing:' + spacing)
	var transform = pick('text-transform')
	if (transform) parts.push('text-transform:' + transform)

	return parts.join(';') + ';'
}

/**
 * Input:  testo, link, colore, colore del testo, raggio, se piccolo, campo, variante ('outline' o pieno)
 * Fa:     bottone come link a blocco. A contorno il fondo resta quello della sezione, e bordo e
 *         testo prendono il colore del bottone: il colore del testo non si usa, altrimenti il
 *         bianco di default sparirebbe su un fondo chiaro. Il bordo toglie 2px di padding per
 *         lato, cosi' le due varianti hanno la stessa misura
 * Output: HTML del link, vuoto senza testo
 */
function buttonHtml(text, href, bg, color, radius, small, field, variant) {
	if (!text) return ''
	var outline = variant === 'outline'
	// numero anche se arriva come "30px"; vuoto o illeggibile = il 4 di sempre
	var corner = parseInt(radius, 10)
	if (isNaN(corner) || corner < 0) corner = 4
	var fill = escapeHtml(bg || BRAND_COLOR)
	var padding = outline ? (small ? '6px 18px' : '10px 30px') : (small ? '8px 20px' : '12px 32px')
	var colors = outline
		? 'color:' + fill + ';background-color:transparent;border:2px solid ' + fill + ';'
		: 'color:' + escapeHtml(color || '#ffffff') + ';background-color:' + fill + ';'
	return '<a' + df(field) + ' href="' + escapeHtml(href || '#') + '" target="_blank" style="display:inline-block;padding:' + padding + ';font-family:' + DEFAULT_FONT + ';font-size:' + (small ? '14px' : '16px') + ';line-height:1.2;font-weight:700;' + colors + 'border-radius:' + corner + 'px;text-decoration:none;">' + escapeHtml(text) + '</a>'
}

function buttonStyleField(section) {
	return { key: 'buttonStyle', type: 'select', label: 'Stile bottone', section: section, options: [{ text: 'Pieno', value: 'filled' }, { text: 'Solo contorno', value: 'outline' }] }
}

function buttonRadiusField(section) {
	return { key: 'buttonRadius', type: 'number', label: 'Angoli del bottone (30 = a pillola)', section: section }
}

/**
 * Input:  block, testo, colore di default
 * Fa:     occhiello: riga corta sopra il titolo, maiuscola e spaziata. Segue la scala del corpo
 *         (font e colore), la spaziatura fra lettere e' sua se il blocco non ne fissa una
 * Output: HTML, vuoto senza testo
 */
function eyebrowHtml(block, text, color) {
	if (!text) return ''
	var style = textStyle(block, { size: '13px', color: color, weight: '700', margin: '0 0 10px 0', align: 'center' })
	if (!styleValue(block, 'letter-spacing', false)) style += 'letter-spacing:2px;'
	return '<p data-field="eyebrow" style="' + style + '">' + escapeHtml(String(text).toUpperCase()) + '</p>'
}

/**
 * Input:  testo, colore di fondo, campo
 * Fa:     etichetta a pillola ("Novita'", "#1"). Il testo segue la luminanza del fondo e non il
 *         colore del testo dei bottoni: coi bottoni a contorno quello non si usa e vale bianco
 * Output: HTML, vuoto senza testo
 */
function badgeHtml(text, bg, field, keepRoom) {
	if (!text && !keepRoom) return ''
	var fill = bg || BRAND_COLOR
	// senza etichetta ma in una riga che ne ha: stesso ingombro, invisibile, cosi' i nomi restano allineati
	var colors = text
		? 'background-color:' + escapeHtml(fill) + ';color:' + (isDarkColor(fill) ? '#ffffff' : '#1a1a1a') + ';'
		: 'background-color:transparent;color:transparent;'
	return '<p style="margin:12px 0 0 0;text-align:center;font-size:0;line-height:0;"><span' + (text ? df(field) : '') + ' style="display:inline-block;padding:3px 10px;border-radius:10px;' + colors + 'font-family:' + DEFAULT_FONT + ';font-size:11px;line-height:1.4;font-weight:700;letter-spacing:1px;">' + (text ? escapeHtml(String(text).toUpperCase()) : '&nbsp;') + '</span></p>'
}

// data-field: marca gli elementi di testo modificabili con doppio click nel canvas (EmailBlock)
function df(field) {
	return field ? ' data-field="' + field + '"' : ''
}

/**
 * Input:  forma richiesta ('rounded' | 'circle' | altro), larghezza del riquadro
 * Fa:     angoli arrotondati o cerchio. Il motore di Word non conosce border-radius: su
 *         Outlook per Windows l'immagine resta quadrata, che e' meno bello ma non e' rotto.
 *         Per il cerchio serve un riquadro quadrato, quindi si fissa anche l'altezza
 * Output: pezzo di stile inline
 */
function shapeStyle(shape, width) {
	if (shape === 'rounded') return 'border-radius:8px;'
	if (shape === 'circle') return 'border-radius:50%;height:' + width + 'px;object-fit:cover;'
	return ''
}

function imgHtml(src, alt, width, extraStyle) {
	return '<img src="' + escapeHtml(src) + '" alt="' + escapeHtml(alt) + '" width="' + width + '" style="display:block;width:100%;max-width:' + width + 'px;height:auto;border:0;' + (extraStyle || '') + '" />'
}

var EMAIL_BODY_WIDTH = 600

/**
 * Input:  block
 * Fa:     larghezza davvero disponibile dentro il blocco: la mail e' larga 600 (o la colonna in
 *         cui sta il blocco, vedi fitToColumns), ma padding e bordo del blocco la riducono. Le colonne affiancate hanno larghezze fisse in px
 *         e senza questo conto la terza colonna finisce a capo appena qualcuno da' aria al
 *         blocco — succede da solo, senza errori, e non si capisce perche'
 * Output: px disponibili
 */
function availableWidth(block) {
	var s = block.styles || {}
	var horizontal = 0

	if (s['padding-left'] || s['padding-right']) {
		horizontal = (parseInt(s['padding-left'], 10) || 0) + (parseInt(s['padding-right'], 10) || 0)
	} else if (s.padding) {
		var parts = String(s.padding).trim().split(/\s+/).map(function(v) { return parseInt(v, 10) || 0 })
		// scorciatoia CSS: 1 valore = tutti i lati, 2 = verticale/orizzontale, 3 = alto/orizz./basso, 4 = alto/dx/basso/sx
		if (parts.length === 1) horizontal = parts[0] * 2
		else if (parts.length === 2 || parts.length === 3) horizontal = parts[1] * 2
		else if (parts.length >= 4) horizontal = parts[1] + parts[3]
	}

	if (s['border-style'] && s['border-style'] !== 'none') horizontal += 2 * (parseInt(s['border-width'], 10) || 0)

	return Math.max(120, (block.containerWidth || EMAIL_BODY_WIDTH) - horizontal)
}

/**
 * Input:  cells [{ width, html }] con somma larghezze <= larghezza disponibile
 * Fa:     colonne affiancate su desktop e impilate sui telefoni senza media query
 * Output: HTML della riga
 */
function hybridRow(cells) {
	var total = 0
	for (var t = 0; t < cells.length; t++) total += cells[t].width
	var out = '<!--[if mso]><table role="presentation" width="' + total + '" cellpadding="0" cellspacing="0" border="0"><tr><![endif]-->'
	for (var i = 0; i < cells.length; i++) {
		out += '<!--[if mso]><td width="' + cells[i].width + '" valign="top" style="padding:0;"><![endif]-->'
		// font-size/line-height ripristinati: il contenitore li azzera per togliere gli spazi fra inline-block
		out += '<div style="display:inline-block;width:100%;max-width:' + cells[i].width + 'px;vertical-align:top;font-size:14px;line-height:1.5;">' + cells[i].html + '</div>'
		out += '<!--[if mso]></td><![endif]-->'
	}
	out += '<!--[if mso]></tr></table><![endif]-->'
	return '<div style="text-align:center;font-size:0;line-height:0;">' + out + '</div>'
}

/**
 * Input:  colore esadecimale (#rgb o #rrggbb)
 * Fa:     luminanza percepita, per scegliere un testo che si legga sopra quel fondo
 * Output: true se il fondo e' scuro
 */
function isDarkColor(hex) {
	var h = String(hex || '').replace('#', '')
	if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
	if (!/^[0-9a-fA-F]{6}$/.test(h)) return true
	var r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16)
	return (0.299 * r + 0.587 * g + 0.114 * b) < 140
}

/**
 * Input:  block dell'hero, HTML dei testi, HTML del bottone
 * Fa:     fascia con l'immagine di sfondo e il testo sopra. Il motore di Word non sa disegnare
 *         un background-image: per Outlook si passa da un rettangolo VML (v:rect + v:fill),
 *         chiuso nei commenti condizionali cosi' gli altri client non lo vedono.
 *         Due reti di sicurezza, perche' l'immagine puo' non arrivare (Outlook non scarica le
 *         immagini finche' non glielo si dice): l'attributo bgcolor tiene il colore pieno sotto
 *         il testo, e il VML si emette solo per una vera URL — un data: URI li' non funziona.
 * Output: HTML della fascia
 */
function heroOverlay(block, textsHtml, btnHtml) {
	var s = block.settings
	var bg = (block.styles || {})['background-color'] || '#222222'
	var height = parseInt(s.overlayHeight, 10) || 360
	var src = s.imageSrc || ''
	var isUrl = /^https?:\/\//i.test(src)

	// Solo una URL vera diventa sfondo: il segnaposto del catalogo e' un SVG con scritto
	// "Immagine 600 x 200", e da sfondo si leggerebbe dietro al titolo. Cosi' l'editor mostra
	// la stessa fascia colorata che ricevera' chi apre la mail senza immagini
	var background = 'background-color:' + escapeHtml(bg) + ';'
	if (isUrl) background += 'background-image:url(\'' + escapeHtml(src) + '\');background-repeat:no-repeat;background-position:center center;background-size:cover;'

	var content = '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>'
		+ '<td style="padding:40px 30px;text-align:center;">' + textsHtml
		+ (btnHtml ? '<div style="margin:20px 0 0 0;">' + btnHtml + '</div>' : '')
		+ '</td></tr></table>'

	var vmlOpen = isUrl
		? '<!--[if gte mso 9]><v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:600px;height:' + height + 'px;">'
			+ '<v:fill type="frame" src="' + escapeHtml(src) + '" color="' + escapeHtml(bg) + '" />'
			+ '<v:textbox inset="0,0,0,0"><![endif]-->'
		: ''
	var vmlClose = isUrl ? '<!--[if gte mso 9]></v:textbox></v:rect><![endif]-->' : ''

	return '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>'
		+ '<td' + (isUrl ? ' background="' + escapeHtml(src) + '"' : '') + ' bgcolor="' + escapeHtml(bg) + '"'
		+ ' valign="middle" height="' + height + '" style="' + background + '">'
		+ vmlOpen + content + vmlClose
		+ '</td></tr></table>'
}

var SOCIAL_ICON_BASE = 'https://app.givemehive.cloud/img/social/'
var SOCIAL_NETWORKS = [
	{ key: 'facebook', alt: 'Facebook' },
	{ key: 'twitter', alt: 'Twitter' },
	{ key: 'linkedin', alt: 'LinkedIn' },
	{ key: 'instagram', alt: 'Instagram' },
	{ key: 'youtube', alt: 'YouTube' },
	{ key: 'pinterest', alt: 'Pinterest' }
]

function socialFields(section) {
	return [
		{ key: 'facebook', type: 'url', label: 'Facebook', icon: 'mdi-facebook', section: section },
		{ key: 'twitter', type: 'url', label: 'Twitter / X', icon: 'mdi-twitter', section: section },
		{ key: 'linkedin', type: 'url', label: 'LinkedIn', icon: 'mdi-linkedin', section: section },
		{ key: 'instagram', type: 'url', label: 'Instagram', icon: 'mdi-instagram', section: section },
		{ key: 'youtube', type: 'url', label: 'YouTube', icon: 'mdi-youtube', section: section },
		{ key: 'pinterest', type: 'url', label: 'Pinterest', icon: 'mdi-pinterest', section: section },
		{ type: 'hint', text: 'Lascia vuoto l\'URL per nascondere l\'icona.', section: section }
	]
}

function socialDefaults() {
	return { facebook: '', twitter: '', linkedin: '', instagram: '', youtube: '', pinterest: '' }
}

// Segnaposto del footer trascinato a mano: per il controllo dei dati legali valgono come vuoti
var FOOTER_PLACEHOLDERS = {
	companyName: 'Nome Azienda S.r.l.',
	address: 'Via Esempio 1, 00100 Roma (RM)',
	vat: 'P.IVA 00000000000'
}

// Frase prima di "puoi disattivarlo quando vuoi": neutra di proposito, perche' il footer e' lo
// stesso per chi ha il tracciamento attivo e per chi l'ha disattivato o non ha dato il consenso
var TRACKING_TEXT = 'Le nostre email possono contenere un pixel di tracciamento:'

// "06730460489" diventa "P.IVA 06730460489"; chi ha gia' scritto l'etichetta la tiene
function labelled(value, label, needsLabel) {
	var v = String(value || '').trim()
	if (!v) return ''
	return (needsLabel.test(v) ? label + ' ' : '') + escapeHtml(v)
}

/**
 * Input:  settings di un footer-full
 * Fa:     elenca i dati legali mancanti per una campagna vera: identita' del mittente e sede
 *         (D.Lgs. 70/2003 art. 7), REA e, per S.r.l./S.p.A., capitale sociale (art. 2250 c.c.),
 *         informativa privacy. I segnaposto del catalogo contano come vuoti
 * Output: array di etichette leggibili, vuoto se il footer e' completo
 */
export function legalGaps(settings) {
	var s = settings || {}
	var filled = function(key) {
		var v = String(s[key] || '').trim()
		return v !== '' && v !== FOOTER_PLACEHOLDERS[key]
	}
	var gaps = []
	if (!filled('companyName')) gaps.push('ragione sociale')
	if (!filled('address')) gaps.push('sede legale')
	if (!filled('vat')) gaps.push('partita IVA')
	if (!filled('rea')) gaps.push('numero REA')
	// La forma societaria viene dopo il nome: in testa e' una parola ("Spa Wellness Center").
	// Col segnaposto ("Nome Azienda S.r.l.") manca gia' la ragione sociale, non anche il capitale
	if (filled('companyName') && /\s(s\.?\s?r\.?\s?l\.?\s?s?\.?|s\.?\s?p\.?\s?a\.?)(?=[\s,;)]|$)/i.test(s.companyName) && !filled('shareCapital')) gaps.push('capitale sociale')
	if (!filled('privacyHref')) gaps.push('link all\'informativa privacy')
	return gaps
}

function socialIconsHtml(settings, size) {
	var links = []
	for (var i = 0; i < SOCIAL_NETWORKS.length; i++) {
		var n = SOCIAL_NETWORKS[i]
		if (!settings[n.key]) continue
		links.push('<a href="' + escapeHtml(settings[n.key]) + '" target="_blank" style="display:inline-block;padding:5px;text-decoration:none;">' +
			'<img src="' + SOCIAL_ICON_BASE + n.key + '.png" alt="' + n.alt + '" width="' + size + '" height="' + size + '" style="display:block;border:0;" /></a>')
	}
	return links.join(' ')
}

// Segnaposto inline (SVG): nessuna dipendenza da servizi esterni.
var IMAGE_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MDAiIGhlaWdodD0iMjAwIj48cmVjdCB3aWR0aD0iNjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2UwZTBlMCIvPjx0ZXh0IHg9IjMwMCIgeT0iMTA3IiBmb250LWZhbWlseT0iQXJpYWwsSGVsdmV0aWNhLHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjAiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkltbWFnaW5lIDYwMCB4IDIwMDwvdGV4dD48L3N2Zz4K'
var LOGO_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxODAiIGhlaWdodD0iNjAiPjxyZWN0IHdpZHRoPSIxODAiIGhlaWdodD0iNjAiIHJ4PSI0IiBmaWxsPSIjZTBlMGUwIi8+PHRleHQgeD0iOTAiIHk9IjM3IiBmb250LWZhbWlseT0iQXJpYWwsSGVsdmV0aWNhLHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTgiIGZvbnQtd2VpZ2h0PSI3MDAiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkxPR088L3RleHQ+PC9zdmc+Cg=='

function layoutDef(type, label, icon, widths, description) {
	return {
		type: type,
		label: label,
		icon: icon,
		description: description,
		isLayout: true,
		defaults: function() {
			return {
				columns: widths.map(function(w) { return { width: w, children: [] } }),
				styles: { 'background-color': '#ffffff' }
			}
		}
	}
}

function newProduct(name) {
	return { imageSrc: IMAGE_PLACEHOLDER, badge: '', name: name || 'Nome prodotto', price: '€ 0,00', buttonText: 'Acquista', buttonHref: '' }
}

export var blockCategories = [
	{
		name: 'Sezioni',
		icon: 'mdi-view-agenda-outline',
		blocks: [
			{
				type: 'header',
				label: 'Intestazione',
				icon: 'mdi-page-layout-header',
				description: 'Prima riga della mail: logo del brand con link al sito. Sempre per prima, una sola.',
				fields: [
					{ key: 'logoSrc', type: 'image', label: 'Logo' },
					{ key: 'logoWidth', type: 'number', label: 'Larghezza logo' },
					{ key: 'logoAlt', type: 'text', label: 'Testo alternativo' },
					{ key: 'href', type: 'url', label: 'Link al sito' },
					{ key: 'align', type: 'align', label: 'Allineamento' }
				],
				defaults: function() {
					return {
						styles: { padding: '0', 'background-color': '#ffffff' },
						settings: { logoSrc: LOGO_PLACEHOLDER, logoWidth: '180', logoAlt: 'Logo', href: '', align: 'center' }
					}
				},
				render: function(block) {
					var s = block.settings
					var align = s.align === 'left' || s.align === 'right' ? s.align : 'center'
					var margin = align === 'center' ? 'margin:0 auto;' : align === 'right' ? 'margin:0 0 0 auto;' : 'margin:0;'
					var w = parseInt(s.logoWidth, 10) || 180
					var img = '<img src="' + escapeHtml(s.logoSrc || LOGO_PLACEHOLDER) + '" alt="' + escapeHtml(s.logoAlt) + '" width="' + w + '" style="display:block;max-width:100%;height:auto;border:0;' + margin + '" />'
					if (s.href) img = '<a href="' + escapeHtml(s.href) + '" target="_blank" style="display:inline-block;text-decoration:none;">' + img + '</a>'
					return tableWrap('text-align:' + align + ';padding:20px 15px;', img)
				}
			},
			{
				type: 'hero',
				label: 'Hero',
				icon: 'mdi-image-text',
				description: 'Apertura d\'impatto: immagine grande, titolo, frase breve, bottone. Subito dopo l\'intestazione, una sola per mail.',
				fields: [
					{ key: 'layout', type: 'select', label: 'Disposizione', options: [
						{ text: 'Immagine sopra, testo sotto', value: 'standard' },
						{ text: 'Testo sopra l\'immagine', value: 'overlay' }
					] },
					{ key: 'imageSrc', type: 'image', label: 'Immagine' },
					{ key: 'imageAlt', type: 'text', label: 'Testo alternativo' },
					{ key: 'overlayHeight', type: 'number', label: 'Altezza fascia (solo con testo sopra)' },
					{ key: 'imagePrompt', type: 'textarea', label: 'Prompt per generare l\'immagine', section: 'Generazione immagine' },
					{ type: 'hint', text: 'Descrizione da dare a un generatore di immagini per ottenere questa foto. Non finisce nella mail: serve a chi la prepara.', section: 'Generazione immagine' },
					{ type: 'hint', text: 'Con il testo sopra l\'immagine scegli un colore di sfondo scuro e un testo chiaro: dove le immagini non si caricano (Outlook le blocca finche\' non le si scarica) resta leggibile solo grazie a quello.' },
					{ key: 'eyebrow', type: 'text', label: 'Occhiello (riga corta sopra il titolo)', section: 'Testi' },
					{ key: 'title', type: 'text', label: 'Titolo', section: 'Testi' },
					{ key: 'text', type: 'textarea', label: 'Testo', section: 'Testi' },
					{ key: 'buttonText', type: 'text', label: 'Testo bottone', section: 'Bottone' },
					{ key: 'buttonHref', type: 'url', label: 'URL link', section: 'Bottone' },
					{ key: 'buttonColor', type: 'color', label: 'Colore bottone', section: 'Bottone' },
					{ key: 'buttonTextColor', type: 'color', label: 'Colore testo bottone', section: 'Bottone' },
					buttonStyleField('Bottone'),
					buttonRadiusField('Bottone')
				],
				defaults: function() {
					return {
						styles: { padding: '0', 'background-color': '#ffffff' },
						settings: {
							layout: 'standard',
							imageSrc: IMAGE_PLACEHOLDER, imageAlt: '', overlayHeight: '360', imagePrompt: '',
							eyebrow: '',
							title: 'Titolo principale',
							text: 'Una frase che presenta la novità o l\'offerta di questa email.',
							buttonText: 'Scopri di più', buttonHref: '',
							buttonColor: BRAND_COLOR, buttonTextColor: '#ffffff', buttonStyle: 'filled', buttonRadius: '4'
						}
					}
				},
				render: function(block) {
					var s = block.settings
					var btn = buttonHtml(s.buttonText, s.buttonHref, s.buttonColor, s.buttonTextColor, s.buttonRadius, false, 'buttonText', s.buttonStyle)
					var overlay = s.layout === 'overlay'

					// Col testo sopra l'immagine il contrasto non e' un dettaglio estetico: se il
					// fondo e' chiaro un titolo bianco sparisce del tutto dove l'immagine non si
					// carica. Il colore di default segue la luminanza dello sfondo del blocco;
					// una scelta esplicita in titleStyles/styles resta comunque prioritaria.
					var dark = overlay && isDarkColor((block.styles || {})['background-color'] || '#222222')
					var titleColor = overlay ? (dark ? '#ffffff' : '#1a1a1a') : '#222222'
					var textColor = overlay ? (dark ? '#f0f0f0' : '#333333') : '#555555'

					var title = s.title ? '<h1 data-field="title" style="' + textStyle(block, { size: overlay ? '38px' : '26px', color: titleColor, weight: '700', margin: '0 0 12px 0', align: 'center', heading: true }) + '">' + escapeHtml(caseText(block, true, s.title)) + '</h1>' : ''
					var text = s.text ? '<p data-field="text" style="' + textStyle(block, { size: '16px', color: textColor, align: 'center' }) + '">' + nl2br(caseText(block, false, s.text)) + '</p>' : ''

					var eyebrow = eyebrowHtml(block, s.eyebrow, textColor)

					if (overlay) return heroOverlay(block, eyebrow + title + text, btn)

					var rows = ''
					if (s.imageSrc) rows += '<tr><td style="padding:0;">' + imgHtml(s.imageSrc, s.imageAlt, availableWidth(block)) + '</td></tr>'
					rows += '<tr><td style="padding:30px 30px 10px 30px;">' + eyebrow + title + text + '</td></tr>'
					if (btn) rows += '<tr><td style="padding:10px 30px 30px 30px;text-align:center;">' + btn + '</td></tr>'
					return '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">' + rows + '</table>'
				}
			},
			{
				type: 'article',
				label: 'Articolo',
				icon: 'mdi-newspaper-variant-outline',
				description: 'Notizia o approfondimento: immagine a lato, titolo, testo breve, link \'leggi tutto\'. Ripetibile, alternando la posizione dell\'immagine.',
				fields: [
					{ key: 'imageSrc', type: 'image', label: 'Immagine' },
					{ key: 'imageAlt', type: 'text', label: 'Testo alternativo' },
					{ key: 'imagePosition', type: 'select', label: 'Posizione immagine', options: [{ text: 'Sinistra', value: 'left' }, { text: 'Destra', value: 'right' }] },
					{ key: 'shape', type: 'select', label: 'Forma immagine', options: [{ text: 'Quadrata', value: 'square' }, { text: 'Angoli arrotondati', value: 'rounded' }, { text: 'Cerchio', value: 'circle' }] },
					{ key: 'imagePrompt', type: 'textarea', label: 'Prompt per generare l\'immagine', section: 'Generazione immagine' },
					{ type: 'hint', text: 'Descrizione da dare a un generatore di immagini per ottenere questa foto. Non finisce nella mail: serve a chi la prepara.', section: 'Generazione immagine' },
					{ key: 'title', type: 'text', label: 'Titolo', section: 'Testi' },
					{ key: 'text', type: 'textarea', label: 'Testo', section: 'Testi' },
					{ key: 'linkText', type: 'text', label: 'Testo del link', section: 'Link' },
					{ key: 'linkHref', type: 'url', label: 'URL link', section: 'Link' },
					{ key: 'linkColor', type: 'color', label: 'Colore del link', section: 'Link' }
				],
				defaults: function() {
					return {
						styles: { padding: '0', 'background-color': '#ffffff' },
						settings: {
							imageSrc: IMAGE_PLACEHOLDER, imageAlt: '', imagePosition: 'left', shape: 'square', imagePrompt: '',
							title: 'Titolo dell\'articolo',
							text: 'Due o tre frasi che raccontano la notizia e invitano a leggere il resto sul sito.',
							linkText: 'Leggi tutto', linkHref: '', linkColor: BRAND_COLOR
						}
					}
				},
				render: function(block) {
					var s = block.settings
					var avail = availableWidth(block)
					var imageWidth = Math.round(avail * 0.4)
					var imageCell = { width: imageWidth, html: '<div style="padding:20px 15px;">' + imgHtml(s.imageSrc || IMAGE_PLACEHOLDER, s.imageAlt, imageWidth - 30, shapeStyle(s.shape, imageWidth - 30)) + '</div>' }
					var link = s.linkText ? '<p style="' + textStyle(block, { size: '15px', color: '#333333', margin: '12px 0 0 0' }) + '"><a href="' + escapeHtml(s.linkHref || '#') + '" target="_blank" data-field="linkText" style="color:' + escapeHtml(s.linkColor || BRAND_COLOR) + ';font-weight:700;text-decoration:none;">' + escapeHtml(caseText(block, false, s.linkText)) + ' &rarr;</a></p>' : ''
					var textCell = { width: avail - imageWidth, html: '<div style="padding:20px 15px;">' +
						(s.title ? '<h2 data-field="title" style="' + textStyle(block, { size: '20px', color: '#222222', weight: '700', margin: '0 0 8px 0', heading: true }) + '">' + escapeHtml(caseText(block, true, s.title)) + '</h2>' : '') +
						(s.text ? '<p data-field="text" style="' + textStyle(block, { size: '15px', color: '#555555' }) + '">' + nl2br(caseText(block, false, s.text)) + '</p>' : '') +
						link + '</div>' }
					return hybridRow(s.imagePosition === 'right' ? [textCell, imageCell] : [imageCell, textCell])
				}
			},
			{
				type: 'products',
				label: 'Griglia prodotti',
				icon: 'mdi-view-grid-outline',
				description: 'Vetrina di prodotti o servizi in griglia (2 o 3 per riga) con foto, nome, prezzo e bottone.',
				fields: [
					{ key: 'columns', type: 'select', label: 'Prodotti per riga', options: [{ text: '2', value: '2' }, { text: '3', value: '3' }] },
					{ key: 'shape', type: 'select', label: 'Forma immagini', options: [{ text: 'Quadrata', value: 'square' }, { text: 'Angoli arrotondati', value: 'rounded' }, { text: 'Cerchio', value: 'circle' }] },
					{ key: 'buttonColor', type: 'color', label: 'Colore bottoni' },
					{ key: 'buttonTextColor', type: 'color', label: 'Colore testo bottoni' },
					buttonStyleField(),
					buttonRadiusField(),
					{
						key: 'items', type: 'list', label: 'Prodotti', itemLabel: 'Prodotto', min: 1, max: 12,
						newItem: function() { return newProduct() },
						fields: [
							{ key: 'imageSrc', type: 'image', label: 'Immagine' },
							{ key: 'badge', type: 'text', label: 'Etichetta sopra il nome (es. Novità)' },
							{ key: 'name', type: 'text', label: 'Nome' },
							{ key: 'price', type: 'text', label: 'Prezzo' },
							{ key: 'buttonText', type: 'text', label: 'Testo bottone' },
							{ key: 'buttonHref', type: 'url', label: 'URL link' }
						]
					}
				],
				defaults: function() {
					return {
						styles: { padding: '10px 0', 'background-color': '#ffffff' },
						settings: { columns: '2', shape: 'square', buttonColor: BRAND_COLOR, buttonTextColor: '#ffffff', buttonStyle: 'filled', buttonRadius: '4', items: [newProduct('Primo prodotto'), newProduct('Secondo prodotto')] }
					}
				},
				render: function(block) {
					var s = block.settings
					var cols = parseInt(s.columns, 10) === 3 ? 3 : 2
					var cellWidth = Math.floor(availableWidth(block) / cols)
					var items = s.items || []
					var rows = ''
					for (var i = 0; i < items.length; i += cols) {
						var cells = []
						var rowBadge = items.slice(i, i + cols).some(function(item) { return !!item.badge })
						for (var c = 0; c < cols; c++) {
							var it = items[i + c]
							cells.push({ width: cellWidth, html: it ? '<div style="padding:15px 10px;">' +
								imgHtml(it.imageSrc || IMAGE_PLACEHOLDER, it.name, cellWidth - 20, 'margin:0 auto;' + shapeStyle(s.shape, cellWidth - 20)) +
								badgeHtml(it.badge, s.buttonColor, 'items.' + (i + c) + '.badge', rowBadge) +
								'<p' + df('items.' + (i + c) + '.name') + ' style="' + textStyle(block, { size: '15px', color: '#222222', weight: '700', margin: (rowBadge ? '8px' : '12px') + ' 0 4px 0', align: 'center' }) + '">' + escapeHtml(caseText(block, false, it.name)) + '</p>' +
								(it.price ? '<p' + df('items.' + (i + c) + '.price') + ' style="' + textStyle(block, { size: '14px', color: s.buttonColor || BRAND_COLOR, weight: '700', margin: '0 0 10px 0', align: 'center' }) + '">' + escapeHtml(caseText(block, false, it.price)) + '</p>' : '') +
								'<div style="text-align:center;">' + buttonHtml(it.buttonText, it.buttonHref, s.buttonColor, s.buttonTextColor, s.buttonRadius, true, 'items.' + (i + c) + '.buttonText', s.buttonStyle) + '</div>' +
								'</div>' : '&nbsp;' })
						}
						rows += hybridRow(cells)
					}
					return rows || '&nbsp;'
				}
			},
			{
				type: 'banner',
				label: 'Banner colorato',
				icon: 'mdi-bullhorn-outline',
				description: 'Fascia a colore pieno per un\'offerta o un annuncio: titolo, testo, bottone. Al massimo una o due per mail.',
				fields: [
					{ key: 'eyebrow', type: 'text', label: 'Occhiello (riga corta sopra il titolo)' },
					{ key: 'title', type: 'text', label: 'Titolo' },
					{ key: 'text', type: 'textarea', label: 'Testo' },
					{ key: 'buttonText', type: 'text', label: 'Testo bottone', section: 'Bottone' },
					{ key: 'buttonHref', type: 'url', label: 'URL link', section: 'Bottone' },
					{ key: 'buttonColor', type: 'color', label: 'Colore bottone', section: 'Bottone' },
					{ key: 'buttonTextColor', type: 'color', label: 'Colore testo bottone', section: 'Bottone' },
					buttonStyleField('Bottone'),
					buttonRadiusField('Bottone'),
					{ type: 'hint', text: 'Il colore di sfondo si cambia nella sezione Sfondo, quello del testo in Tipografia.' }
				],
				defaults: function() {
					return {
						styles: { padding: '0', 'background-color': BRAND_COLOR },
						settings: {
							eyebrow: '', title: 'Offerta speciale', text: 'Solo per pochi giorni: approfittane subito.',
							buttonText: 'Approfitta ora', buttonHref: '',
							buttonColor: '#ffffff', buttonTextColor: BRAND_COLOR, buttonStyle: 'filled', buttonRadius: '4'
						}
					}
				},
				render: function(block) {
					var s = block.settings
					var btn = buttonHtml(s.buttonText, s.buttonHref, s.buttonColor, s.buttonTextColor, s.buttonRadius, false, 'buttonText', s.buttonStyle)
					return tableWrap('padding:35px 30px;text-align:center;',
						eyebrowHtml(block, s.eyebrow, '#ffffff') +
						(s.title ? '<h2 data-field="title" style="' + textStyle(block, { size: '24px', color: '#ffffff', weight: '700', margin: '0 0 10px 0', align: 'center', heading: true }) + '">' + escapeHtml(caseText(block, true, s.title)) + '</h2>' : '') +
						(s.text ? '<p data-field="text" style="' + textStyle(block, { size: '16px', color: '#ffffff', align: 'center', margin: btn ? '0 0 20px 0' : '0' }) + '">' + nl2br(caseText(block, false, s.text)) + '</p>' : '') +
						btn)
				}
			},
			{
				type: 'footer-full',
				label: 'Footer completo',
				// Chiude la mail: il contratto AI non accoda blocchi dopo (aiContract.insertAt)
				closing: true,
				icon: 'mdi-page-layout-footer',
				description: 'Chiusura obbligatoria: ragione sociale, sede legale, partita IVA, REA e capitale sociale, contatti, social, link a informativa privacy e termini, motivo della mail, disiscrizione e link per disattivare il tracciamento delle aperture. Sempre per ultimo.',
				fields: [
					{ key: 'logoSrc', type: 'image', label: 'Logo (facoltativo)', section: 'Azienda' },
					{ key: 'companyName', type: 'text', label: 'Ragione sociale', section: 'Azienda' },
					{ key: 'address', type: 'text', label: 'Sede legale', section: 'Azienda' },
					{ key: 'vat', type: 'text', label: 'Partita IVA', section: 'Azienda' },
					{ key: 'rea', type: 'text', label: 'Numero REA (es. FI-123456)', section: 'Azienda' },
					{ key: 'shareCapital', type: 'text', label: 'Capitale sociale (es. € 10.000 i.v.)', section: 'Azienda' },
					{ key: 'email', type: 'text', label: 'Email', section: 'Azienda' },
					{ key: 'phone', type: 'text', label: 'Telefono', section: 'Azienda' }
				].concat(socialFields('Social'), [
					{ key: 'privacyHref', type: 'url', label: 'Link all\'informativa privacy', section: 'Note legali' },
					{ key: 'termsHref', type: 'url', label: 'Link a termini e condizioni', section: 'Note legali' },
					{ key: 'reasonText', type: 'text', label: 'Perché si riceve la mail', section: 'Note legali' },
					{ type: 'hint', text: 'REA e capitale sociale sono obbligatori per le società (art. 2250 c.c.); con uno sconto o un omaggio servono anche le condizioni.', section: 'Note legali' },
					{ key: 'unsubscribeText', type: 'text', label: 'Testo prima del link', section: 'Disiscrizione' },
					{ type: 'hint', text: 'Il link "Disiscriviti" viene inserito sempre: è obbligatorio per legge, insieme all\'indirizzo fisico.', section: 'Disiscrizione' },
					{ key: 'trackingText', type: 'text', label: 'Testo prima del link al tracciamento', section: 'Disiscrizione' },
					{ type: 'hint', text: 'Segue sempre "puoi disattivarlo quando vuoi" con il link: lo chiedono le linee guida del Garante sui pixel nelle email. Se il tracciamento è disattivato nelle impostazioni, la riga non viene inviata.', section: 'Disiscrizione' }
				]),
				defaults: function() {
					return {
						styles: { padding: '0', 'background-color': '#f5f5f5' },
						settings: Object.assign({
							logoSrc: '',
							companyName: FOOTER_PLACEHOLDERS.companyName,
							address: FOOTER_PLACEHOLDERS.address,
							vat: FOOTER_PLACEHOLDERS.vat,
							rea: '', shareCapital: '',
							email: '', phone: '',
							privacyHref: '', termsHref: '',
							reasonText: 'Ricevi questa email perché sei iscritto alla nostra newsletter.',
							unsubscribeText: 'Non vuoi più ricevere queste email?',
							trackingText: TRACKING_TEXT
						}, socialDefaults())
					}
				},
				render: function(block) {
					var s = block.settings
					var line = function(html, extra, field, attrs) { return '<p' + df(field) + (attrs || '') + ' style="' + textStyle(block, Object.assign({ size: '12px', color: '#777777', align: 'center', margin: '0 0 6px 0' }, extra || {})) + '">' + html + '</p>' }
					var link = function(href, text) { return '<a href="' + escapeHtml(href) + '" target="_blank" style="color:#555555;text-decoration:underline;">' + text + '</a>' }
					var social = socialIconsHtml(s, 32)
					var logo = s.logoSrc && s.logoSrc.indexOf('data:') !== 0
						? '<img src="' + escapeHtml(s.logoSrc) + '" alt="' + escapeHtml(s.companyName || 'Logo') + '" width="120" style="display:block;max-width:120px;height:auto;border:0;margin:0 auto 15px auto;" />'
						: ''
					var legal = [labelled(s.vat, 'P.IVA', /^(IT)?\d/i), labelled(s.rea, 'REA', /^(?!rea\b)/i), labelled(s.shareCapital, 'Capitale sociale', /^(?!capitale)/i)]
					var contacts = [s.email, s.phone].filter(Boolean).map(escapeHtml).join(' &middot; ')
					var links = [s.privacyHref ? link(s.privacyHref, 'Informativa privacy') : '', s.termsHref ? link(s.termsHref, 'Termini e condizioni') : ''].filter(Boolean).join(' &middot; ')
					return tableWrap('padding:25px 15px;text-align:center;',
						logo +
						(social ? '<div style="margin:0 0 15px 0;">' + social + '</div>' : '') +
						(s.companyName ? line(escapeHtml(caseText(block, false, s.companyName)), { weight: '700', color: '#555555' }, 'companyName') : '') +
						(s.address ? line(escapeHtml(caseText(block, false, s.address)), null, 'address') : '') +
						(legal.some(Boolean) ? line(legal.filter(Boolean).join(' &middot; ')) : '') +
						(contacts ? line(contacts) : '') +
						(links ? line(links, { margin: '12px 0 6px 0' }) : '') +
						(s.reasonText ? line(escapeHtml(s.reasonText), { margin: links ? '0 0 6px 0' : '12px 0 6px 0' }, 'reasonText') : '') +
						line((s.unsubscribeText ? '<span data-field="unsubscribeText">' + escapeHtml(s.unsubscribeText) + '</span> ' : '') + '<a href="{{UNSUBSCRIBED}}" target="_blank" style="color:#555555;text-decoration:underline;">Disiscriviti</a>', { margin: (links || s.reasonText) ? '0 0 6px 0' : '12px 0 6px 0' }) +
						// Sempre presente, come la disiscrizione: senza testo torna la frase di default.
						// Il marcatore la fa togliere all'invio se il tenant spegne il tracciamento
						line('<span data-field="trackingText">' + escapeHtml(s.trackingText || TRACKING_TEXT) + '</span> puoi <a href="{{TRACKING_PREFERENCES}}" target="_blank" style="color:#555555;text-decoration:underline;">disattivarlo</a> quando vuoi.', { margin: '0 0 6px 0' }, null, ' data-tracking-notice="1"') +
						line('&copy; {{ANNO_CORRENTE}} ' + escapeHtml(s.companyName || ''), { margin: '0' }))
				}
			}
		]
	},
	{
		name: 'Struttura',
		icon: 'mdi-view-column-outline',
		blocks: [
			layoutDef('section-1col', '1 Colonna', 'mdi-square-outline', ['100%'], 'Contenitore a una colonna per raggruppare elementi con uno sfondo comune.'),
			layoutDef('section-2col', '2 Colonne', 'mdi-view-column', ['50%', '50%'], 'Contenitore a due colonne uguali: elementi affiancati.'),
			layoutDef('section-3col', '3 Colonne', 'mdi-view-column-outline', ['33.33%', '33.33%', '33.34%'], 'Contenitore a tre colonne uguali.'),
			layoutDef('section-sidebar', '2 Colonne (30/70)', 'mdi-view-split-vertical', ['30%', '70%'], 'Contenitore con colonna stretta a sinistra (30%) e larga a destra (70%).')
		]
	},
	{
		name: 'Elementi',
		icon: 'mdi-shape-outline',
		// Gli elementi non hanno uno sfondo proprio: dentro una colonna mostrano quello della
		// sezione, al primo livello il bianco della mail. Uno sfondo di default li staccherebbe
		// come riquadri bianchi su ogni sezione colorata
		blocks: [
			{
				type: 'text',
				label: 'Testo',
				icon: 'mdi-format-text',
				description: 'Paragrafo di testo libero in HTML semplice.',
				defaults: function() {
					return {
						content: '<p style="margin:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#333333;">Scrivi il tuo testo qui. Puoi modificarlo direttamente facendo doppio click.</p>',
						styles: { padding: '15px' },
						editable: true
					}
				}
			},
			{
				type: 'heading',
				label: 'Titolo + Testo',
				icon: 'mdi-format-header-1',
				description: 'Titolo seguito da un paragrafo, in HTML semplice.',
				defaults: function() {
					return {
						content: '<h1 style="margin:0 0 10px 0;font-family:Arial,Helvetica,sans-serif;font-size:24px;font-weight:700;color:#222222;">Titolo della sezione</h1>' +
							'<p style="margin:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#555555;">Paragrafo di testo descrittivo. Modifica questo contenuto con le tue informazioni.</p>',
						styles: { padding: '15px' },
						editable: true
					}
				}
			},
			{
				type: 'image',
				label: 'Immagine',
				icon: 'mdi-image',
				description: 'Immagine singola a tutta larghezza o ridotta, con link opzionale.',
				fields: [
					{ key: 'src', type: 'image', label: 'Immagine', widthKey: 'width', maxWidth: 600 },
					{ key: 'alt', type: 'text', label: 'Testo alternativo' },
					{ key: 'width', type: 'number', label: 'Larghezza' },
					{ key: 'shape', type: 'select', label: 'Forma', options: [{ text: 'Quadrata', value: 'square' }, { text: 'Angoli arrotondati', value: 'rounded' }, { text: 'Cerchio', value: 'circle' }] },
					{ key: 'align', type: 'align', label: 'Allineamento' },
					{ key: 'href', type: 'url', label: 'Link (opzionale)' },
					{ key: 'imagePrompt', type: 'textarea', label: 'Prompt per generare l\'immagine', section: 'Generazione immagine' },
					{ type: 'hint', text: 'Descrizione da dare a un generatore di immagini per ottenere questa foto. Non finisce nella mail: serve a chi la prepara.', section: 'Generazione immagine' }
				],
				defaults: function() {
					return {
						styles: { padding: '0' },
						settings: { src: IMAGE_PLACEHOLDER, alt: 'Immagine', width: '600', shape: 'square', align: 'center', href: '', target: '_blank', imagePrompt: '' }
					}
				},
				render: function(block) {
					var s = block.settings
					var align = s.align === 'left' || s.align === 'right' ? s.align : 'center'
					var marginStyle = align === 'center' ? 'margin:0 auto;' : align === 'right' ? 'margin:0 0 0 auto;' : 'margin:0;'
					// Il motore di Word rispetta l'attributo width anche oltre la colonna: ci si ferma alla larghezza disponibile
					var width = Math.min(parseInt(s.width, 10) || 600, availableWidth(block))
					var img = '<img src="' + escapeHtml(s.src || IMAGE_PLACEHOLDER) + '" alt="' + escapeHtml(s.alt) + '" width="' + width + '" style="display:block;max-width:100%;height:auto;border:0;' + marginStyle + shapeStyle(s.shape, width) + '" />'
					if (s.href) {
						img = '<a href="' + escapeHtml(s.href) + '" target="' + escapeHtml(s.target || '_blank') + '" style="display:inline-block;text-decoration:none;">' + img + '</a>'
					}
					return tableWrap('text-align:' + align + ';padding:0;', img)
				}
			},
			{
				type: 'button',
				label: 'Bottone',
				icon: 'mdi-card-outline',
				description: 'Bottone di invito all\'azione, centrato.',
				fields: [
					{ key: 'text', type: 'text', label: 'Testo bottone' },
					{ key: 'href', type: 'url', label: 'URL link' },
					{ key: 'buttonColor', type: 'color', label: 'Colore bottone' },
					{ key: 'textColor', type: 'color', label: 'Colore testo' },
					{ key: 'borderRadius', type: 'number', label: 'Angoli del bottone (30 = a pillola)' },
					buttonStyleField()
				],
				defaults: function() {
					return {
						styles: { padding: '0' },
						settings: { text: 'Bottone', href: 'https://example.com', target: '_blank', buttonColor: BRAND_COLOR, textColor: '#ffffff', borderRadius: '4', buttonStyle: 'filled' }
					}
				},
				render: function(block) {
					var s = block.settings
					return tableWrap('text-align:center;padding:20px 15px;', buttonHtml(s.text || 'Bottone', s.href, s.buttonColor, s.textColor, s.borderRadius, false, 'text', s.buttonStyle))
				}
			},
			{
				type: 'divider',
				label: 'Separatore',
				icon: 'mdi-minus',
				description: 'Linea orizzontale di separazione.',
				fields: [
					{ key: 'color', type: 'color', label: 'Colore linea' },
					{ key: 'thickness', type: 'number', label: 'Spessore' },
					{ key: 'style', type: 'select', label: 'Stile', options: ['solid', 'dashed', 'dotted'] }
				],
				defaults: function() {
					return {
						styles: { padding: '0' },
						settings: { color: '#dddddd', thickness: '1', style: 'solid' }
					}
				},
				render: function(block) {
					var s = block.settings
					return tableWrap('padding:10px 15px;',
						'<hr style="border:0;border-top:' + escapeHtml(s.thickness || '1') + 'px ' + escapeHtml(s.style || 'solid') + ' ' + escapeHtml(s.color || '#dddddd') + ';margin:0;" />')
				}
			},
			{
				type: 'spacer',
				label: 'Spaziatore',
				icon: 'mdi-arrow-expand-vertical',
				description: 'Spazio verticale vuoto.',
				fields: [
					{ key: 'height', type: 'number', label: 'Altezza' }
				],
				defaults: function() {
					return {
						styles: { padding: '0' },
						settings: { height: '30' }
					}
				},
				render: function(block) {
					var h = escapeHtml(block.settings.height || '30')
					return tableWrap('height:' + h + 'px;font-size:0;line-height:0;', '&nbsp;')
				}
			},
			{
				type: 'social-links',
				label: 'Link Social',
				icon: 'mdi-share-variant',
				description: 'Fila di icone social con link.',
				fields: socialFields(),
				defaults: function() {
					return {
						styles: { padding: '0' },
						settings: Object.assign(socialDefaults(), {
							facebook: 'https://facebook.com', twitter: 'https://twitter.com',
							linkedin: 'https://linkedin.com', instagram: 'https://instagram.com'
						})
					}
				},
				render: function(block) {
					return tableWrap('text-align:center;padding:15px;', socialIconsHtml(block.settings, 40) || '&nbsp;')
				}
			},
			{
				type: 'unsubscribe-link',
				label: 'Link Disiscrizione',
				icon: 'mdi-link-off',
				description: 'Solo il link di disiscrizione, per chi non usa il footer completo.',
				defaults: function() {
					return {
						content: '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr><td style="text-align:center;padding:10px 15px;">' +
							'<a href="{{UNSUBSCRIBED}}" target="_blank" style="font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#999999;text-decoration:underline;">Disiscriviti dalla mailing list</a>' +
							'</td></tr></table>',
						styles: { padding: '0' }
					}
				}
			},
			{
				type: 'tracking-preferences-link',
				label: 'Link Tracciamento',
				icon: 'mdi-eye-off-outline',
				description: 'Solo il link per disattivare il tracciamento delle aperture, per chi non usa il footer completo.',
				defaults: function() {
					return {
						content: '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr><td style="text-align:center;padding:10px 15px;">' +
							'<a data-tracking-notice="1" href="{{TRACKING_PREFERENCES}}" target="_blank" style="font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#999999;text-decoration:underline;">Disattiva il tracciamento</a>' +
							'</td></tr></table>',
						styles: { padding: '0' }
					}
				}
			},
			{
				// Sostituito da "Footer completo": resta nel catalogo solo per i template gia' salvati
				type: 'unsubscribe-footer',
				label: 'Footer Disiscrizione',
				closing: true,
				icon: 'mdi-email-off-outline',
				hidden: true,
				defaults: function() {
					return {
						content: '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>' +
							'<td style="padding:20px 15px;text-align:center;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#999999;line-height:1.5;">' +
							'<p style="margin:0 0 5px 0;"><i>Copyright &copy; {{ANNO_CORRENTE}}, Tutti i diritti sono riservati.</i></p>' +
							'<p style="margin:0 0 5px 0;">Il tuo indirizzo mail è: {{AZIENDA_EMAIL_PRIMARIA}}</p>' +
							'<p style="margin:0;">Non vuoi più ricevere queste mail? <a href="{{UNSUBSCRIBED}}" target="_blank" style="color:#333333;text-decoration:underline;">Disiscriviti</a></p>' +
							'</td></tr></table>',
						styles: { padding: '0', 'background-color': '#f5f5f5' },
						editable: true
					}
				}
			}
		]
	}
]

export function findBlockDef(type) {
	for (var i = 0; i < blockCategories.length; i++) {
		for (var j = 0; j < blockCategories[i].blocks.length; j++) {
			if (blockCategories[i].blocks[j].type === type) {
				return blockCategories[i].blocks[j]
			}
		}
	}
	return null
}

// Proporzioni che il generatore di immagini accetta, con la resa dentro il corpo mail
// (largo 600). Il backend le rivalida (config/ai.php, builder_image.aspect_ratios): questa lista
// deve restarne un sottoinsieme.
export var imageRatios = [
	{ text: 'Fascia hero (4:1) - 600x150', value: '4:1' },
	{ text: 'Panoramica larga (16:9) - 600x338', value: '16:9' },
	{ text: 'Panoramica (3:2) - 600x400', value: '3:2' },
	{ text: 'Standard (4:3) - 600x450', value: '4:3' },
	{ text: 'Quadrata (1:1) - 600x600', value: '1:1' },
	{ text: 'Verticale (4:5) - 600x750', value: '4:5' },
	{ text: 'Verticale alta (2:3) - 600x900', value: '2:3' }
]

// Proporzione di partenza quando l'assistente chiede l'immagine senza indicarla
var DEFAULT_RATIO = { hero: '16:9', article: '1:1', image: '3:2' }

/**
 * Input:  type di un blocco
 * Fa:     dice se quel blocco puo' ricevere un'immagine generata, e su quale campo.
 *         Il criterio e' la presenza di `imagePrompt` nel catalogo: i blocchi che devono
 *         portare una foto REALE — il logo dell'intestazione, gli articoli del listino
 *         prodotti — non ce l'hanno, e restano fuori da soli senza un elenco a parte
 * Output: { srcKey, ratio } oppure null
 */
export function imageGenSpec(type) {
	var def = findBlockDef(type)
	if (!def || !def.fields) return null

	var srcKey = null
	var hasPrompt = false
	for (var i = 0; i < def.fields.length; i++) {
		if (def.fields[i].key === 'imagePrompt') hasPrompt = true
		if (def.fields[i].type === 'image' && !srcKey) srcKey = def.fields[i].key
	}

	if (!hasPrompt || !srcKey) return null
	return { srcKey: srcKey, ratio: DEFAULT_RATIO[type] || '3:2' }
}

export function allBlockDefs() {
	var result = []
	for (var i = 0; i < blockCategories.length; i++) {
		for (var j = 0; j < blockCategories[i].blocks.length; j++) {
			result.push(blockCategories[i].blocks[j])
		}
	}
	return result
}

/**
 * Input:  type — tipo di blocco del catalogo
 * Fa:     istanzia un blocco nuovo con i default del tipo e, se ha `render`, il content gia' generato
 * Output: blocco pronto per il canvas, o null se il tipo non esiste
 */
/** Il blocco rende un titolo proprio: ha una seconda scala tipografica (titleStyles) */
export function hasTitleScale(def) {
	if (!def || !def.fields) return false
	for (var i = 0; i < def.fields.length; i++) {
		if (def.fields[i].key === 'title') return true
	}
	return false
}

export function createBlock(type) {
	var def = findBlockDef(type)
	if (!def) return null
	var block = Object.assign({ id: uid(), type: type }, def.defaults())
	if (hasTitleScale(def) && !block.titleStyles) block.titleStyles = {}
	if (def.render) block.content = def.render(block)
	return block
}

/**
 * Input:  block — blocco con settings/styles aggiornati
 * Fa:     rigenera il content per i tipi con `render`
 * Output: HTML del content (invariato per i blocchi senza `render`)
 */
export function renderBlockContent(block) {
	var def = findBlockDef(block.type)
	return def && def.render ? def.render(block) : block.content
}

/**
 * Input:  blocks del documento
 * Fa:     scrive su ogni figlio di una colonna la larghezza vera della colonna (containerWidth),
 *         tolti padding e bordo della sezione, e rigenera il content dei blocchi a cui cambia.
 *         Il render di un blocco non vede il suo contenitore: senza questo un articolo in una
 *         colonna calcola le sue celle su 600px e il testo finisce sotto la foto, e un'immagine
 *         larga 600 sfora la colonna su Outlook. Al primo livello torna la larghezza della mail
 * Output: gli stessi blocks, aggiornati sul posto
 */
export function fitToColumns(blocks) {
	for (var i = 0; i < (blocks || []).length; i++) {
		var block = blocks[i]
		if (!block.columns) {
			setContainerWidth(block, null)
			continue
		}
		var inner = availableWidth(block)
		for (var c = 0; c < block.columns.length; c++) {
			var width = Math.floor(inner * (parseFloat(block.columns[c].width) || 100) / 100)
			var children = block.columns[c].children || []
			for (var k = 0; k < children.length; k++) setContainerWidth(children[k], width)
		}
	}
	return blocks
}

function setContainerWidth(block, width) {
	if ((block.containerWidth || null) === width) return
	if (width) block.containerWidth = width
	else delete block.containerWidth
	var def = findBlockDef(block.type)
	if (def && def.render) block.content = def.render(block)
}

/**
 * Input:  block — blocco (anche layout con colonne e figli)
 * Fa:     deep-clone con id nuovi a ogni livello, cosi' il duplicato non condivide id con l'originale
 * Output: blocco clonato
 */
export function cloneBlockDeep(block) {
	var clone = JSON.parse(JSON.stringify(block))
	reassignIds(clone)
	return clone
}

function reassignIds(block) {
	block.id = uid()
	if (!block.columns) return
	for (var c = 0; c < block.columns.length; c++) {
		var children = block.columns[c].children || []
		for (var i = 0; i < children.length; i++) reassignIds(children[i])
	}
}

/**
 * Fonts safe for email clients
 */
// Solo famiglie installate di serie su Windows e Mac: nelle mail i web font li carica una
// minoranza di client (Apple Mail si', Gmail e Outlook no), quindi qui non entrano.
// Ogni voce ha la sua catena di ripiego, per ultimo il generico.
export var emailFonts = [
	{ text: 'Arial', value: 'Arial,Helvetica,sans-serif' },
	{ text: 'Century Gothic (geometrico)', value: '\'Century Gothic\',CenturyGothic,AppleGothic,\'Trebuchet MS\',sans-serif' },
	{ text: 'Courier New', value: '\'Courier New\',Courier,monospace' },
	{ text: 'Garamond (elegante)', value: 'Garamond,\'Palatino Linotype\',\'Times New Roman\',serif' },
	{ text: 'Georgia', value: 'Georgia,serif' },
	{ text: 'Helvetica', value: 'Helvetica,Arial,sans-serif' },
	{ text: 'Impact', value: 'Impact,Charcoal,sans-serif' },
	{ text: 'Lucida Sans', value: '\'Lucida Sans Unicode\',\'Lucida Grande\',Geneva,Verdana,sans-serif' },
	{ text: 'Palatino (serif morbido)', value: 'Palatino,\'Palatino Linotype\',\'Book Antiqua\',Georgia,serif' },
	{ text: 'Tahoma', value: 'Tahoma,Geneva,sans-serif' },
	{ text: 'Times New Roman', value: '\'Times New Roman\',Times,serif' },
	{ text: 'Trebuchet MS (umanistico)', value: '\'Trebuchet MS\',Tahoma,Geneva,sans-serif' },
	{ text: 'Verdana', value: 'Verdana,Geneva,sans-serif' }
]
